import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store';
import { requestVerificationCode, clearError } from '../store/authSlice';
import ClickSparkWrapper from '../components/common/ClickSparkWrapper';
import Particles from '../components/common/Particles';
import { BrowserSupportIndicator } from '../components/common';
import { loginEmailConfig, getOptimizedConfig } from '../components/common/ParticleConfig';
import { vibeService } from '../services/VibeService';
import { resolvePostLoginPath } from '../utils/onboardingNavigation';
import './Login.css';

const LoginEmail: React.FC = () => {
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  
  const dispatch = useAppDispatch();
  const { isLoading, error, isAuthenticated } = useAppSelector((state) => state.auth);

  // Redirect if already authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    const redirectAuthenticatedUser = async () => {
      const path = await resolvePostLoginPath();
      if (!cancelled) {
        navigate(path);
      }
    };

    redirectAuthenticatedUser();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, navigate]);

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.trim()) {
      return;
    }

    try {
      // Store email in sessionStorage for the code verification page
      sessionStorage.setItem('loginEmail', email);
      
      // Make API call to request verification code
      await dispatch(requestVerificationCode(email)).unwrap();
      
      vibeService.trigger('success');
      // Navigate to code verification page
      navigate('/login/verify-code');
    } catch (err) {
      console.error('Failed to send verification code:', err);
      // Still navigate to code page for demo mode
      sessionStorage.setItem('loginEmail', email);
      sessionStorage.setItem('demoMode', 'true');
      navigate('/login/verify-code');
    }
  };

  // Get optimized particle configuration for this device
  const particleConfig = getOptimizedConfig(loginEmailConfig);

  return (
    <>
    <div className="simple-login-page" style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Particle Background */}
      <Particles {...particleConfig} />
      
      <div className="login-content">
        {/* Logo */}
        <div className="logo">
          <img src="/image_i_do1.png" alt="Luci logo" className="luci-logo" />
        </div>

        <h1>Sign in</h1>
        <p className="instructions">
          Enter your email address to receive a signin code
        </p>

        <form onSubmit={handleEmailSubmit} className="email-form">
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) {
                dispatch(clearError());
              }
            }}
            placeholder="Enter your email address"
            className="email-input"
            disabled={isLoading}
            autoFocus
          />

          {error && (
            <div className="error-text">
              {error}
            </div>
          )}

          {isLoading && (
            <div className="loading-text">
              Sending code...
            </div>
          )}

          <ClickSparkWrapper sparkColor="#a3e635" sparkCount={6} sparkRadius={12} disabled={isLoading || !email}>
            <button
              type="submit"
              className="submit-button"
              disabled={isLoading || !email}
            >
              {isLoading ? 'Sending...' : 'Send Code'}
            </button>
          </ClickSparkWrapper>
        </form>
      </div>
    </div>
    
    {/* Browser compatibility indicator - outside overflow:hidden container */}
    <BrowserSupportIndicator />
    </>
  );
};

export default LoginEmail;
